'use client'

import { ColumnDef } from '@tanstack/react-table'
import { CustomerAccount } from '../types'
import { CustomerAccountActions } from './CustomerAccountActions'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useDemoMode } from '@/context/DemoModeContext'

export const createAllCustomerAccountsColumns = (
  onEdit: (account: CustomerAccount) => void
): ColumnDef<CustomerAccount>[] => {
  const navigate = useNavigate()

  return [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const { isDemoMode } = useDemoMode()
        return (
          <div className={`font-medium ${isDemoMode ? 'blur-sm select-none' : ''}`}>
            {row.getValue('email')}
          </div>
        )
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const { isDemoMode } = useDemoMode()
        const name = row.getValue('name') as string
        return (
          <div className={isDemoMode ? 'blur-sm select-none' : ''}>
            {name || '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'customer',
      header: 'Customer Code',
      cell: ({ row }) => {
        const { isDemoMode } = useDemoMode()
        const customer = row.getValue('customer') as CustomerAccount['customer']
        const accountCode = customer?.account_code

        if (!accountCode) {
          return <div className="font-mono">-</div>
        }

        return (
          <Button
            variant="link"
            className={`h-auto p-0 font-mono text-left ${isDemoMode ? 'blur-sm select-none' : ''}`}
            onClick={() => {
              navigate(
                `/dashboard/b2b-accounts?search=${encodeURIComponent(
                  accountCode
                )}`
              )
            }}
          >
            {accountCode}
          </Button>
        )
      },
    },
    {
      accessorKey: 'customer',
      header: 'Customer Name',
      cell: ({ row }) => {
        const { isDemoMode } = useDemoMode()
        const customer = row.getValue('customer') as CustomerAccount['customer']
        const customerName =
          customer?.b2b_company_name ||
          (customer?.name && customer?.surname
            ? `${customer.name} ${customer.surname}`
            : customer?.name)
        return (
          <div className={isDemoMode ? 'blur-sm select-none' : ''}>
            {customerName || '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'customerSubAccount',
      header: 'Location',
      cell: ({ row }) => {
        const customerSubAccount = row.getValue(
          'customerSubAccount'
        ) as CustomerAccount['customerSubAccount']
        return <div>{customerSubAccount?.name || '-'}</div>
      },
    },
    {
      accessorKey: 'is_prices_enabled',
      header: 'Prices',
      cell: ({ row }) => {
        // Fail open: a missing value means the account can see prices.
        const enabled = (row.getValue('is_prices_enabled') as boolean) ?? true
        return (
          <div className={enabled ? 'text-gray-900' : 'text-red-600'}>
            {enabled ? 'Visible' : 'Hidden'}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => {
        const date = new Date(row.getValue('createdAt'))
        return <div>{date.toLocaleDateString()}</div>
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        return (
          <CustomerAccountActions
            customerAccount={row.original}
            onEdit={onEdit}
          />
        )
      },
    },
  ]
}
